import React, { useMemo } from "react"
import Image from "next/image"

import { startOfWeek, endOfWeek, useUpdateableState } from "@lib"

const WeekDayView: React.FunctionComponent<{
    date: Date,
    timeZone?: string,
    itemCount?: (date: Date) => number,
    onClick?: (date: Date) => void
}> = ({ date, timeZone, itemCount, onClick }) => {
    const hasItems = itemCount ? itemCount(date) > 0 : false

    const weekdayClassName = useMemo(() => {
        const now = new Date()
        now.setHours(0, 0, 0, 0)

        const classNames: string[] = ["text-callout"]

        if (date.getTime() === now.getTime()) {
            classNames.push(...[
                hasItems ? "bg-primary-900" : "bg-black-500",
                "rounded-[4px] p-[2px] text-white-900"
            ])
        } else if (hasItems) {
            classNames.push("text-primary-900")
        } else {
            classNames.push("text-black-500")
        }

        return classNames.join(" ")
    }, [date, hasItems])

    const dateClassName = useMemo(() => {
        const now = new Date()
        now.setHours(0, 0, 0, 0)

        if (!hasItems) {
            return "text-title1 text-black-500"
        } else {
            return "text-title1 text-primary-900"
        }
    }, [hasItems])

    return (
        <div className={["min-w-[80px] sm:flex-1 p-[8px] text-center uppercase"].concat(hasItems ? ["cursor-pointer"] : []).join(" ")}
            onClick={() => onClick && onClick(date)}>
            <div className={weekdayClassName}>{date.toLocaleDateString("en-US", { weekday: "short", timeZone })}</div>
            <div className={dateClassName}>{date.toLocaleDateString("en-US", { day: "numeric", timeZone })}</div>
        </div>
    )
}

const WeekView: React.FunctionComponent<{
    className?: string
    minDate?: Date,
    maxDate?: Date,
    from?: Date
    to?: Date
    timeZone?: string,
    itemCount?: (date: Date) => number
    onDayClick?: (date: Date) => void
    onChange?: (from: Date, to: Date) => void
}> = ({ className, minDate = new Date(), maxDate, from, to, timeZone, itemCount, onDayClick, onChange }) => {
    const [fromDate, setFromDate] = useUpdateableState(from || startOfWeek(new Date(), timeZone))
    const [toDate, setToDate] = useUpdateableState(to || endOfWeek(new Date(), timeZone))

    const previousWeek = () => {
        if (minDate && fromDate!.getTime() <= minDate.getTime()) {
            return
        }

        const nextFrom = new Date(fromDate!.getTime() - (7 * 86400 * 1000))
        const nextTo = endOfWeek(nextFrom)
        setFromDate(nextFrom)
        setToDate(nextTo)

        onChange && onChange(nextFrom, nextTo)
    }

    const nextWeek = () => {
        if (maxDate && toDate!.getTime() === maxDate.getTime()) {
            return
        }

        const nextFrom = new Date(fromDate!.getTime() + (7 * 86400 * 1000))
        const nextTo = endOfWeek(nextFrom)
        setFromDate(nextFrom)
        setToDate(nextTo)

        onChange && onChange(nextFrom, nextTo)
    }

    return (
        <div id="weekView" className={className}>
            <div className="flex">
                <div id="weekView.dateRangeStepper" className="flex items-center mx-auto gap-x-[12px]">
                    <button className="w-[48px] h-[48px]" onClick={() => previousWeek()}>
                        <div className="m-auto w-[24px] h-[24px]">
                            <Image width="24" height="24" src={fromDate!.getTime() <= minDate.getTime() ? "/images/ic_chevron_left_disabled_big.svg" : "/images/ic_chevron_left_accent.svg"} alt="Previous" />
                        </div>
                    </button>

                    <div className="text-headline uppercase">{fromDate!.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone })} - {toDate!.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone })}</div>

                    <button className="w-[48px] h-[48px]" onClick={() => nextWeek()}>
                        <div className="m-auto w-[24px] h-[24px]">
                            <Image width="24" height="24" src={maxDate && toDate!.getTime() === maxDate?.getTime() ? "/images/ic_chevron_right_disabled_big.svg" : "/images/ic_chevron_right_accent.svg"} alt="Next" />
                        </div>
                    </button>
                </div>
            </div>

            <div className="flex w-full mt-[24px] gap-x-[12px] overflow-auto overscroll-contain">
                {[0, 1, 2, 3, 4, 5, 6].map((each) => {
                    const date = new Date(fromDate!.getTime() + (each * 86400 * 1000))
                    return (<WeekDayView date={date} key={each} itemCount={itemCount} onClick={onDayClick} />)
                })}
            </div>
        </div>
    )
}

export default WeekView
